<script lang="ts">
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	//Helpers
	import { capFirstChar } from '../../../../helpers/utilities';
	import { HTTPS_CERTIFICATE_DOCS_URL } from '../../../../helpers/device_settings_helpers';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';

	//Stores
	import { rebootRequired } from 'src/stores/device-runtime/states';

	//Design system components
	import {
		Button,
		Modal,
		Text,
		Container,
		toast,
		FileUpload,
		type FileUploadPropsTypes,
		type FileUploadState,
	} from '@Shelly/shelly-design-system';

	export let device;
	export let showModal = false;

	const CHUNK_SIZE = 1024;

	type CertSection = {
		titleKey: string;
		titleDefault: string;
		descriptionKey?: string;
		descriptionDefault?: string;
		uploadMethod: string;
		file: File | null;
		loading: boolean;
		uploaded: boolean;
		uploadedFileName: string;
	};

	let sections: CertSection[] = [
		{
			titleKey: 'tls_server_cert_title',
			titleDefault: 'HTTPS server certificate',
			descriptionKey: 'tls_server_cert_description',
			descriptionDefault: 'Upload a certificate to enable HTTPS access to your device.',
			uploadMethod: 'Shelly.PutHTTPServerCert',
			file: null,
			loading: false,
			uploaded: false,
			uploadedFileName: '',
		},
		{
			titleKey: 'tls_server_key_title',
			titleDefault: 'HTTPS server key',
			uploadMethod: 'Shelly.PutHTTPServerKey',
			file: null,
			loading: false,
			uploaded: false,
			uploadedFileName: '',
		},
		{
			titleKey: 'tls_server_ca_title',
			titleDefault: 'HTTPS server CA bundle (mutual TLS)',
			descriptionKey: 'tls_server_ca_description',
			descriptionDefault:
				'Upload a CA certificate to require client certificate authentication (mutual TLS).',
			uploadMethod: 'Shelly.PutHTTPServerCABundle',
			file: null,
			loading: false,
			uploaded: false,
			uploadedFileName: '',
		},
	];

	let needsReboot = false;
	let sending = false;
	let certificateFileUploadTexts: FileUploadPropsTypes['texts'];

	$: hasStagedFiles = sections.some((s) => s.file && !s.uploaded);
	$: certAndKeyReady =
		Boolean(sections[0]?.file || sections[0]?.uploaded) &&
		Boolean(sections[1]?.file || sections[1]?.uploaded);
	$: canSend = hasStagedFiles && certAndKeyReady;
	$: certificateFileUploadTexts = {
		dropzone: $format('tls_file_upload_dropzone', {
			default: 'Drop certificate files here',
		}),
		browse: $format('tls_file_upload_browse', {
			default: 'Browse certificates',
		}),
		remove: $format('tls_remove_button', {
			default: 'Remove',
		}),
		removeAll: $format('tls_file_upload_remove_all', {
			default: 'Remove all files',
		}),
		replace: $format('tls_replace_button', {
			default: 'Replace',
		}),
		clear: $format('tls_clear_button', {
			default: 'Clear',
		}),
		retry: $format('tls_file_upload_retry', {
			default: 'Try again',
		}),
	};

	const getUploadFiles = (section: CertSection) => {
		if (section.file) return [section.file];
		if (section.uploaded && section.uploadedFileName) {
			return [{ name: section.uploadedFileName } as File];
		}

		return [];
	};

	const getUploadState = (section: CertSection): FileUploadState => {
		if (section.loading) return 'uploading';
		if (section.uploaded) return 'uploaded';
		if (section.file) return 'staged';

		return 'idle';
	};

	const resetSection = (index: number) => {
		sections[index].file = null;
	};

	const handleFilesChange = (index: number, files: File[]) => {
		if (files.length) {
			sections[index].uploaded = false;
			sections[index].uploadedFileName = '';
			sections[index].file = files[0];
			return;
		}

		if (!sections[index].uploaded) {
			resetSection(index);
		}
	};

	const readFileAsText = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = () => reject(reader.error);
			reader.readAsText(file);
		});
	};

	const uploadChunked = async (method: string, content: string) => {
		let res = null;

		for (let offset = 0; offset < content.length; offset += CHUNK_SIZE) {
			res = await gen2Request(device, method, {
				data: content.substring(offset, offset + CHUNK_SIZE),
				append: offset > 0,
			});
			if (!res?.isok) break;
		}

		return res;
	};

	const showErrorToast = (res) => {
		const message =
			res?.errors?.message ||
			$format('setting_err_toast', { default: 'Failed to apply settings.' });
		toast.open({ type: 'danger', title: message });
	};

	const trackRestart = (res) => {
		if (res?.restart_required || res?.data?.restart_required || res?.result?.restart_required) {
			needsReboot = true;
		}
	};

	const handleSendAll = async () => {
		sending = true;
		let success = true;

		for (let i = 0; i < sections.length; i++) {
			const section = sections[i];
			if (!section.file || section.uploaded) continue;

			sections[i].loading = true;

			try {
				const content = await readFileAsText(section.file);
				const res = await uploadChunked(section.uploadMethod, content);

				if (res?.isok) {
					sections[i].uploaded = true;
					sections[i].uploadedFileName = section.file.name;
					resetSection(i);
					trackRestart(res);
				} else {
					success = false;
					showErrorToast(res);
				}
			} catch {
				success = false;
				toast.open({
					type: 'danger',
					title: $format('tls_upload_error_toast', {
						default: 'Failed to read the file.',
					}),
				});
			}

			sections[i].loading = false;
		}

		sending = false;

		if (success) {
			toast.open({
				type: 'success',
				title: $format('tls_send_success_toast', {
					default: 'All certificates sent to device successfully.',
				}),
			});

			showModal = false;
			setSidePanelState({ showSettingsModal: false });
			if (needsReboot) {
				rebootRequired.set(device);
			}
		}
	};

	const handleClear = async (index: number) => {
		sections[index].loading = true;

		const res = await gen2Request(device, sections[index].uploadMethod, {
			data: null,
			append: false,
		});

		if (res?.isok) {
			sections[index].uploaded = false;
			sections[index].uploadedFileName = '';
			resetSection(index);
			trackRestart(res);

			toast.open({
				type: 'success',
				title: $format('tls_clear_success_toast', {
					default: 'Certificate removed successfully.',
				}),
			});
		} else {
			showErrorToast(res);
		}

		sections[index].loading = false;
	};

	const handleClose = () => {
		showModal = false;
		if (needsReboot) {
			rebootRequired.set(device);
		}
	};
</script>

{#if showModal}
	<Modal
		showModal={true}
		on:close={handleClose}
		title={$format('tls_configuration_modal_title', { default: 'TLS Configuration' })}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<Text styleType="body-small" class="mb-s">
					{$format('tls_configuration_description', {
						default:
							'Upload certificates to enable HTTPS access and configure TLS for your device.',
					})}
					{' '}
					<Text
						element="a"
						href={HTTPS_CERTIFICATE_DOCS_URL}
						target="_blank"
						rel="noopener noreferrer"
						class="link-underline d-inline"
						styleType="body-small"
					>
						{$format('tls_learn_more_link', {
							default: 'Learn how to obtain an HTTPS certificate',
						})}
					</Text>
				</Text>

				{#each sections as section, index}
					<div class="mb-m">
						<Text styleType="body-small" class="mb-xs">
							<strong>
								{$format(section.titleKey, { default: section.titleDefault })}
							</strong>
						</Text>

						{#if section.descriptionKey}
							<Text styleType="body-small" class="mb-s">
								{$format(section.descriptionKey, {
									default: section.descriptionDefault,
								})}
							</Text>
						{/if}

						<FileUpload
							files={getUploadFiles(section)}
							state={getUploadState(section)}
							accept=".pem,.crt,.cer,.key"
							disabled={sending}
							texts={certificateFileUploadTexts}
							onFilesChange={(files) => handleFilesChange(index, files)}
							onRemove={() =>
								section.uploaded ? handleClear(index) : resetSection(index)}
						/>
					</div>
				{/each}
			</Container>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end gap-s">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				disabled={sending}
				on:click={handleClose}
			>
				{capFirstChar($format('button_close', { default: 'Close' }))}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				responsiveSize={{ sm: 'm' }}
				variant="primary"
				loading={sending}
				disabled={!canSend}
				on:click={handleSendAll}
			>
				{capFirstChar($format('tls_send_to_device_button', { default: 'Send to device' }))}
			</Button>
		</div>
	</Modal>
{/if}
